import { InputNumber, InputCurrency } from "@sellar-io/components";
import { useEffect } from "react";
import { useWatch, UseFormReturn } from "react-hook-form";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { FulfillmentTypeContainerDeliveryChargeFormType } from "../types/FulfillmentTypeContainerDeliveryCharge";
import { twMerge } from "tailwind-merge";

const SINGLE_CONTAINER_TYPES = ["cask", "steelKeg", "plasticKeg"] as const;

export const FulfillmentTypeContainerDeliveryChargeForm = ({
  form,
  onSubmit,
  formId,
}: {
  form: UseFormReturn<FulfillmentTypeContainerDeliveryChargeFormType>;
  onSubmit: (data: FulfillmentTypeContainerDeliveryChargeFormType) => void;
  formId?: string;
}) => {
  const containerType = useWatch({
    control: form.control,
    name: "containerType",
  });
  const isSingleContainer = SINGLE_CONTAINER_TYPES.includes(containerType);

  useEffect(() => {
    if (isSingleContainer) {
      form.setValue("packQuantity", 1, { shouldValidate: true });
    }
  }, [form, isSingleContainer]);

  return (
    <form
      id={formId}
      onSubmit={form.handleSubmit(onSubmit)}
      className={"flex flex-col gap-2"}
    >
      <InputLabel>Container type</InputLabel>
      <InputContainer>
        <InputSelect {...form.register("containerType", { required: true })}>
          <InputOption value={"bottle"}>Bottle</InputOption>
          <InputOption value={"can"}>Can</InputOption>
          <InputOption value={"cask"}>Cask</InputOption>
          <InputOption value={"steelKeg"}>Steel keg</InputOption>
          <InputOption value={"plasticKeg"}>Plastic keg</InputOption>
          <InputOption value={"pouch"}>Pouch</InputOption>
          <InputOption value={"bagInBox"}>Bag In Box</InputOption>
          <InputOption value={"glass"}>Glass</InputOption>
          <InputOption value={"bag"}>Bag</InputOption>
          <InputOption value={"jar"}>Jar</InputOption>
        </InputSelect>
      </InputContainer>
      <div className={"grid grid-cols-3 gap-2"}>
        <div className="col-span-1">
          <InputLabel>Pack Quantity</InputLabel>
          <InputContainer>
            <InputNumber
              {...form.register("packQuantity", {
                required: "Minimum 1 pack quantity required.",
                valueAsNumber: true,
                min: 1,
              })}
              disabled={isSingleContainer}
              className={twMerge(isSingleContainer && "opacity-50")}
              min={1}
            />
          </InputContainer>
        </div>
        <div className="col-span-1">
          <InputLabel>Volume</InputLabel>
          <InputContainer>
            <InputNumber
              {...form.register("volume", {
                required: "Volume is required.",
                valueAsNumber: true,
                min: 0,
              })}
              step={"0.01"}
              min={0}
            />
          </InputContainer>
        </div>
        <div className="col-span-1">
          <InputLabel>Unit</InputLabel>
          <InputContainer>
            <InputSelect {...form.register("displayUnit", { required: true })}>
              <InputOption value={"gal"}>gal - gallon</InputOption>
              <InputOption value={"L"}>L - litre</InputOption>
              <InputOption value={"ml"}>ml - millilitre</InputOption>
              <InputOption value={"cl"}>cl - centilitre</InputOption>
              <InputOption value={"pt"}>pt - pint</InputOption>
              <InputOption value={"oz"}>oz - ounce</InputOption>
              <InputOption value={"g"}>g - gram</InputOption>
              <InputOption value={"kg"}>kg - kilogram</InputOption>
            </InputSelect>
          </InputContainer>
        </div>
      </div>
      <InputLabel>Delivery charge</InputLabel>
      <InputContainer>
        <InputCurrency
          showPrefix
          allowNegative
          {...form.register("charge", {
            valueAsNumber: true,
            required: "Minimum £0 delivery charge.",
            min: 0,
          })}
          min={0}
        />
      </InputContainer>
    </form>
  );
};
